import { React, useEffect, useState } from "react";
import { Box, Typography, Divider, Button, Grid } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { FINANCIAL_INFORMATION_API, FILTER_API } from "../../api";
import {
  setFinancialInformationData,
  setIsLoading,
  setReloadFinancialInformationData,
  updateFinancialInformationData,
} from "../../slice";
import { useFinancialInfo } from "../../hooks";
import Input from "../Input";
import Select from "../Select";
import BaseCheckbox from "../all-inputs/BaseCheckbox";
import BaseTextArea from "../all-inputs/BaseTextArea";
import { BaseRadioGroup } from "../inputs/RadioGroup";

function FinancialInfo({ hasEditAccess, disabled = false, pageErrors = {} }) {
  const dispatch = useDispatch();
  const { data, reloadPage } = useSelector((state) => state.FinancialInformation);
  const { handleSubmit: handleSubmitForm } = useFinancialInfo({ hasEditAccess });
  const handleEmploymentStatus = (event) => {
    const EMPLOYMENT_SECTIONS = [
      "job",
      "jobCompanyName",
      "phoneNumber",
      "address",
      "jobDescription",
    ];
    dispatch(
      updateFinancialInformationData({
        key: "employmentStatusCode",
        value: event.target.value,
        required: true,
      })
    );
    if (parseInt(event.target.value) === 20002 || event.target.value === "") {
      dispatch(
        updateFinancialInformationData({
          key: "employmentTypeStatusCode",
          value: "",
          required: false,
        })
      );
    }
    if (parseInt(event.target.value) === 20002) {
      dispatch(
        updateFinancialInformationData({
          key: "contractTypeCode",
          value: "",
          required: false,
        })
      );
      dispatch(
        updateFinancialInformationData({
          key: "incomeAmountCode",
          value: "",
          required: false,
        })
      );
      EMPLOYMENT_SECTIONS.forEach((item) =>
        dispatch(
          updateFinancialInformationData({
            key: item,
            value: "",
            required: true,
          })
        )
      );
    }
  };

  const handleHaveACarChange = (event) => {
    dispatch(
      updateFinancialInformationData({
        key: "haveACar",
        value: event.target.checked,
        required: true,
      })
    );
    dispatch(
      updateFinancialInformationData({
        key: "carDescription",
        value: "",
        required: true,
      })
    );
  };

  const handleHaveTutelageChenge = (event) => {
    dispatch(
      updateFinancialInformationData({
        key: "haveTutelage",
        value: event.target.checked,
        required: true,
      })
    );
    dispatch(
      updateFinancialInformationData({
        key: "haveTutelageDescription",
        value: "",
        required: true,
      })
    );
  };

  const { data: lookupsData } = useQuery(
    ["getFilterDataCollection_financialInfo"],
    () => FILTER_API.GetFilterDataCollection(false, [20000, 45000, 22000, 23000, 11000, 12000]),
    {
      staleTime: Infinity,
    }
  );

  const { refetch: getFinancialInformation } = useQuery(
    ["getFinancialInformation"],
    () => FINANCIAL_INFORMATION_API.GetFinancialInformation(),
    {
      onSuccess: ({ data: FinancialInformation }) => {
        if (FinancialInformation.success && FinancialInformation.data) {
          dispatch(setFinancialInformationData(FinancialInformation.data));
        }
        dispatch(setIsLoading(false));
        dispatch(setReloadFinancialInformationData(false));
      },
      onError: () => {
        dispatch(setIsLoading(false));
      },
      staleTime: Infinity,
    }
  );

  useEffect(() => {
    if (reloadPage) getFinancialInformation();
  }, [reloadPage]);

  return (
    <form style={{ width: "100%" }}>
      <Box display="flex" flexDirection="column" width={"100%"} alignItems="flex-end">
        <Grid container flexDirection="row-reverse" sx={{ width: "100%", maxWidth: "800px" }}>
          <Grid xs={12} display="flex" flexDirection="row-reverse">
            <Grid
              item
              container
              xs={12}
              md={6}
              display="flex"
              flexDirection="row-reverse"
              alignItems="center"
            >
              <Grid xs={5} display="flex" justifyContent="flex-end">
                <Typography variant="h1">اطلاعات شغلی</Typography>
              </Grid>
              <Grid
                xs={7}
                display="flex"
                justifyContent="flex-end"
                pr={{ xs: "0.6rem", md: "0.3rem" }}
              >
                <BaseCheckbox
                  label="دانشجو هستم"
                  disabled={disabled}
                  checked={data.isStudying}
                  value="isStudying"
                  onChange={(event) =>
                    dispatch(
                      updateFinancialInformationData({
                        key: "isStudying",
                        value: event.target.checked,
                        required: true,
                      })
                    )
                  }
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6} sx={Style.itemGrid}>
            <Select
              label="وضعیت اشتغال"
              idKey="code"
              id="employmentStatusCode"
              name="employmentStatusCode"
              data={lookupsData?.data?.data[20000]}
              disabled={disabled}
              labelFlex={5}
              flex={7}
              value={data.employmentStatusCode ? data.employmentStatusCode : ""}
              onChange={(event) => handleEmploymentStatus(event)}
              error={"employmentStatusCode" in pageErrors}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6} sx={Style.itemGrid}>
            <Select
              label="نوع اشتغال"
              id="employmentTypeStatusCode"
              name="employmentTypeStatusCode"
              idKey="code"
              disabled={
                !data.employmentStatusCode ||
                disabled ||
                (data.employmentStatusCode && parseInt(data.employmentStatusCode) === 20002)
              }
              data={lookupsData?.data?.data[45000]}
              labelFlex={5}
              flex={7}
              value={data.employmentTypeStatusCode ? data.employmentTypeStatusCode : ""}
              onChange={(event) =>
                dispatch(
                  updateFinancialInformationData({
                    key: "employmentTypeStatusCode",
                    value: event.target.value === "" ? undefined : event.target.value,
                    required: true,
                  })
                )
              }
              error={"employmentTypeStatusCode" in pageErrors}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6} sx={Style.itemGrid}>
            <Input
              label="نام شغل"
              placeholder="نام شغل"
              id="job"
              name="job"
              labelFlex={5}
              flex={7}
              disabled={
                (data.employmentStatusCode && parseInt(data.employmentStatusCode) === 20002) ||
                disabled
              }
              value={data.job}
              onChange={(event) =>
                dispatch(
                  updateFinancialInformationData({
                    key: "job",
                    value: event.target.value,
                    required: true,
                  })
                )
              }
              error={"job" in pageErrors}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6} lg={6} sx={Style.itemGrid}>
            <Select
              label="نوع قرارداد شغلی"
              id="contractTypeCode"
              name="contractTypeCode"
              idKey="code"
              data={lookupsData?.data?.data[22000]}
              disabled={
                (data.employmentStatusCode && parseInt(data.employmentStatusCode) === 20002) ||
                disabled
              }
              labelFlex={5}
              flex={7}
              value={data.contractTypeCode ? data.contractTypeCode : ""}
              onChange={(event) =>
                dispatch(
                  updateFinancialInformationData({
                    key: "contractTypeCode",
                    value: event.target.value === "" ? undefined : event.target.value,
                    required: true,
                  })
                )
              }
              error={"contractTypeCode" in pageErrors}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6} sx={Style.itemGrid}>
            <Select
              label="درآمد ماهیانه"
              id="incomeAmountCode"
              name="incomeAmountCode"
              idKey="code"
              disabled={disabled}
              data={lookupsData?.data?.data[23000]}
              labelFlex={5}
              flex={7}
              value={data.incomeAmountCode ? data.incomeAmountCode : ""}
              onChange={(event) =>
                dispatch(
                  updateFinancialInformationData({
                    key: "incomeAmountCode",
                    value: event.target.value === "" ? undefined : event.target.value,
                    required: true,
                  })
                )
              }
              error={"incomeAmountCode" in pageErrors}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} sx={Style.itemGrid}>
            <Input
              label="نام محل کار"
              placeholder="نام محل کار"
              id="jobCompanyName"
              name="jobCompanyName"
              flex={{ xs: 7, sm: 9.7, md: 9.79, lg: 9.79 }}
              labelFlex={{ xs: 5, sm: 2.5, md: 2.5, lg: 2.5 }}
              disabled={
                (data.employmentStatusCode && parseInt(data.employmentStatusCode) === 20002) ||
                disabled
              }
              value={data.jobCompanyName}
              onChange={(event) =>
                dispatch(
                  updateFinancialInformationData({
                    key: "jobCompanyName",
                    value: event.target.value,
                    required: true,
                  })
                )
              }
              error={"jobCompanyName" in pageErrors}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6} sx={Style.itemGrid}>
            <Input
              label="تلفن محل کار"
              placeholder="تلفن محل کار"
              id="phoneNumber"
              name="phoneNumber"
              type="number"
              labelFlex={5}
              flex={7}
              disabled={
                (data.employmentStatusCode && parseInt(data.employmentStatusCode) === 20002) ||
                disabled
              }
              value={data.phoneNumber}
              onChange={(event) =>
                dispatch(
                  updateFinancialInformationData({
                    key: "phoneNumber",
                    value: event.target.value,
                    required: true,
                  })
                )
              }
              error={"phoneNumber" in pageErrors}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} sx={Style.itemGrid}>
            <BaseTextArea
              label="نشانی محل کار"
              placeholder="نشانی محل کار"
              id="address"
              name="address"
              flex={{ xs: 6.7, sm: 9.6, md: 9.65, lg: 9.65 }}
              labelFlex={{ xs: 5, sm: 2.5, md: 2.5, lg: 2.5 }}
              disabled={
                (data.employmentStatusCode && parseInt(data.employmentStatusCode) === 20002) ||
                disabled
              }
              value={data.address}
              onChange={(event) =>
                dispatch(
                  updateFinancialInformationData({
                    key: "address",
                    value: event.target.value,
                    required: true,
                  })
                )
              }
              error={"address" in pageErrors}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} sx={Style.itemGrid}>
            <BaseTextArea
              label="توضیحات"
              placeholder="در صورتی که شغل دیگری دارید توضیح دهید"
              id="jobDescription"
              name="jobDescription"
              flex={{ xs: 6.7, sm: 9.7, md: 9.65, lg: 9.65 }}
              labelFlex={{ xs: 5, sm: 2.5, md: 2.5, lg: 2.5 }}
              disabled={disabled}
              value={data.jobDescription}
              onChange={(event) =>
                dispatch(
                  updateFinancialInformationData({
                    key: "jobDescription",
                    value: event.target.value,
                    required: true,
                  })
                )
              }
              error={"jobDescription" in pageErrors}
            />
          </Grid>
        </Grid>
        <Box mt="1.3rem" mb="2rem" width="100%">
          <Divider />
        </Box>
        <Grid container flexDirection="row-reverse" sx={{ width: "100%", maxWidth: "800px" }}>
          <Grid item xs={12} sm={12} md={12} lg={12} sx={Style.itemGrid}>
            <Typography variant="h1" my={1} textAlign="right">
              سطح اقتصادی
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6} sx={Style.itemGrid}>
            <Select
              label="سطح اقتصادی"
              id="economicLevelCode"
              name="economicLevelCode"
              disabled={disabled}
              labelFlex={5}
              flex={7}
              idKey="code"
              data={lookupsData?.data?.data[11000]}
              value={data.economicLevelCode}
              onChange={(event) =>
                dispatch(
                  updateFinancialInformationData({
                    key: "economicLevelCode",
                    value: event.target.value,
                    required: true,
                  })
                )
              }
              error={"economicLevelCode" in pageErrors}
            />
          </Grid>
          <Grid
            item
            display="flex"
            justifyContent="flex-end"
            xs={12}
            sm={12}
            md={12}
            lg={12}
            sx={Style.itemGrid}
            pr={1.5}
          >
            <BaseCheckbox
              label="اتومبیل شخصی دارم"
              checked={data.haveACar}
              disabled={disabled}
              value="haveACar"
              onChange={handleHaveACarChange}
            />
          </Grid>
          {data.haveACar && (
            <Grid item xs={12} sm={6} md={6} lg={6} sx={Style.itemGrid}>
              <Input
                label="نام اتومبیل"
                placeholder=""
                id="carDescription"
                name="carDescription"
                flex={7}
                labelFlex={4.95}
                disabled={disabled}
                value={data.carDescription}
                onChange={(event) =>
                  dispatch(
                    updateFinancialInformationData({
                      key: "carDescription",
                      value: event.target.value,
                      required: true,
                    })
                  )
                }
                error={"carDescription" in pageErrors}
              />
            </Grid>
          )}
          <Grid
            item
            display="flex"
            justifyContent="flex-end"
            xs={12}
            sm={12}
            md={12}
            lg={12}
            sx={Style.itemGrid}
            pr={1.5}
          >
            <BaseCheckbox
              label="کفالت یک/چند نفر را به عهده دارم"
              checked={data.haveTutelage}
              disabled={disabled}
              value="haveACar"
              onChange={handleHaveTutelageChenge}
            />
          </Grid>
          {data.haveTutelage && (
            <Grid item xs={12} sm={12} md={12} lg={12} sx={Style.itemGrid}>
              <BaseTextArea
                label="توضیحات"
                placeholder=""
                id="haveTutelageDescription"
                name="haveTutelageDescription"
                flex={{ xs: 7, sm: 9.7, md: 9.65, lg: 9.65 }}
                labelFlex={{ xs: 5, sm: 2.5, md: 2.5, lg: 2.5 }}
                disabled={disabled}
                value={data.haveTutelageDescription}
                onChange={(event) =>
                  dispatch(
                    updateFinancialInformationData({
                      key: "haveTutelageDescription",
                      value: event.target.value,
                      required: true,
                    })
                  )
                }
                error={"haveTutelageDescription" in pageErrors}
              />
            </Grid>
          )}
        </Grid>
        <Box my={3} width="100%">
          <Divider />
        </Box>
        <Grid container flexDirection="row-reverse" sx={{ width: "100%", maxWidth: "800px" }}>
          <BaseRadioGroup
            label="وضعیت سکونت بعد از ازدواج"
            data={lookupsData?.data?.data[12000]}
            disabled={disabled}
            value={data.residenceStatusCode}
            onChange={(event) =>
              dispatch(
                updateFinancialInformationData({
                  key: "residenceStatusCode",
                  value: event.target.value,
                  required: true,
                })
              )
            }
            labelGridSize={{ xs: 12 }}
            error={pageErrors["residenceStatusCode"]}
          />
        </Grid>
        <Box
          width="100%"
          sx={{
            display: "flex",
            flexDirection: { xs: "row", sm: "row", md: "row" },
            justifyContent: "center",
            alignItems: "center",
            pt: 2,
            mt: "2rem",
          }}
        >
          <Button
            name="next"
            type="button"
            variant="m1"
            onClick={() => handleSubmitForm("/personal-info/family-info")}
            sx={{ mr: 1, width: "165px", my: { xs: 1, sm: 0, md: 0 } }}
          >
            بعدی
          </Button>
          <Button
            type="button"
            name="previous"
            variant="m2"
            onClick={() => handleSubmitForm("/personal-info/education")}
            sx={{ mr: 1 }}
          >
            قبلی
          </Button>
        </Box>
      </Box>
    </form>
  );
}

const Style = {
  itemGrid: {
    boxSizing: "border-box",
    paddingLeft: { xs: 0, sm: "0.5rem", md: "1rem", lg: "1rem" },
    marginBottom: "0.75rem",
  },
};

export default FinancialInfo;
